import { useCallback, useEffect, useRef, useState } from "react";
import EmptyState from "@/components/EmptyState";
import Table, { buildActionColumn, TableControls, TableDetails, TableFooter } from "@/components/Table";
import { toDateFormat } from "@/lib/dateHelpers";
import { getStickyColumnClasses } from "@/lib/styleHelpers";
import { renderPostStage } from "./_utils";
import useTableRowMenu, { actionId } from "@/hooks/useTableRowMenu";
import type { Action } from "@/components/MenuButton";
import SearchInput from "@/components/SearchInput";
import DateSelect from "@/components/DateSelect";
import { Controller, useForm } from "react-hook-form";
import { useParams, useSearchWithParams } from "@/hooks/useSearchParams";
import Button from "@/components/Button";
import { useNavigate, Path } from "@/router";
import MultiSelect from "@/components/MultiSelect";
import { ColumnSort } from "@tanstack/react-table";
import useCapabilities, {
  DOMAIN,
  PERMISSION,
  useCanAdminPublishJob,
  useHasCapabilityBool
} from "@/hooks/useCapabilities";
import {
  JOBS_SEARCH_QUERY,
  SEND_REVIEW_REQUEST_MUTATION,
  DELETE_JOB_POST_MUTATION,
  JOB_POST_WITH_APPLICATIONS_QUERY,
  JOB_POST_DETAILS_QUERY
} from "./queries";
import {
  JobPost,
  JobsQueryVariables,
  PostStage,
  ServiceLevel,
  JOB_ACTION_CONFIGS,
  canRevertToDraft,
  HiringStatus,
  JobsQueryOrderByVariables
} from "./types";
import { useQuery, useMutation } from "@apollo/client";
import { DateType } from "react-tailwindcss-datepicker";
import { useLocation } from "react-router-dom";
import SectionAutocomplete from "@/pages/jobs/_components/SectionAutocomplete";
import { UPDATE_JOB_POST_MUTATION } from "@/components/Forms/JobPostForm/queries";
import { showSnackbar } from "@/lib/snackbarUtils";
import SendJobPostReviewModal from "./_components/SendJobPostReviewModal";
import QuickActionDialog from "./_components/QuickActionDialog";
import DownloadCSVDialog from "@/components/DownloadCSVDialog";
import RenewJobPostModal from "./_components/RenewJobPostModal";
import { useAgentFileUpload } from "@/lib/aiHelpers";
import MobileSortDrawer from "./_components/MobileSortDrawer";
import ConvertDocumentToJobModal from "./_components/ConvertDocumentToJobModal";
import useConvertedJobData from "@/hooks/useConvertedJobData";
import { PGAAssistantIcon } from "@/components/CustomSVGIcons/PGAAssistantIcon";
import { sysColors } from "@/assets/theme/colors";
import { generateColumnData, columnHeaders as CSVHeaders } from "./_components/ApplicantReviewsExportButton/utils";
import { formatTotalCompensation } from "@/lib/currencyHelpers";
import usePagination from "@/hooks/usePagination";
import { snakeCase } from "lodash";

interface JobsTableColumnHeaders {
  title: boolean;
  facility: boolean;
  serviceLevel: boolean;
  totalCompensation: boolean;
  employer: boolean;
  owner: boolean;
  postedOn: boolean;
  submissionDeadline: boolean;
  applicantCount: boolean;
  postingStage: boolean;
  jobStatus: boolean;
  section: boolean;
}

interface JobsTableFilterProps {
  postingStage: boolean;
  jobStatus: boolean;
  serviceLevel: boolean;
  postedDateRange: boolean;
  section: boolean;
  owner: boolean;
}

interface JobsTableLayoutProps {
  tableHeading?: string;
  tableDescription: string;
  columnHeaders: JobsTableColumnHeaders;
  filters: JobsTableFilterProps;
  defaultValues?: DefaultFilters;
  defaultSort?: { id: string; desc: boolean }[];
}

interface DefaultFilters {
  searchTerm?: string;
  stage?: string[];
  status?: string[];
  sectionId?: string;
  facilityId?: string;
  ownerId?: string;
  lowerDateRange?: DateType;
  upperDateRange?: DateType;
  employerId?: string;
  serviceLevel?: string[];
}

interface JobPostRow {
  id: string;
  title: string;
  facility: string;
  serviceLevel: ServiceLevel;
  totalCompensation: number | null;
  employer: string;
  owner: string;
  createdAt: string;
  goLiveTime?: string;
  submissionDeadline: string;
  applicantCount: number;
  stage: PostStage;
  status: string;
  applicationUrl: string;
  reviewRequestSent?: string;
}

/**
 * Displays a list of Job Posts in a table format.
 *
 * @param {Object} params - The input parameters.
 * @param {string} [params.tableHeading] - The title text displayed above the table.
 * @param {string} params.tableDescription - The sub-description below the title.
 * @param {Object} params.columnHeaders - JobsTableColumnHeaders. Assign true to a key to display that header.
 * @param {Object} params.filters - JobsTableFilterProps. Assign true to a key to display that filter.
 * @param {Object} params.defaultValues - DefaultFilters. Over-ride default value of applied filters.
 * @param {Object} params.defaultSort - { id: string; desc: boolean }[] – Default sorting of table, where id is the id of the column.
 */
export default function JobsTableLayout({
  tableHeading,
  tableDescription,
  columnHeaders,
  filters,
  defaultValues,
  defaultSort
}: JobsTableLayoutProps) {
  let { params, addSearchParam, deleteSearchParams } = useParams();
  const location = useLocation();
  const conversionCancelledRef = useRef(false);

  if (params.dateRange) {
    const [lower, upper] = params.dateRange && params.dateRange.split(",");
    params.dateRange = {
      lowerDateRange: lower || null,
      upperDateRange: upper || null
    };
  }

  if (defaultValues?.lowerDateRange && defaultValues?.upperDateRange && !params.dateRange) {
    params.dateRange = {
      lowerDateRange: defaultValues.lowerDateRange,
      upperDateRange: defaultValues.upperDateRange
    };
  }

  const { dateRange, section: _section, ...otherParams } = params;
  const defaultStages = defaultValues?.stage || [];
  const defaultServiceLevels = defaultValues?.serviceLevel || [];
  const defaultStatuses = defaultValues?.status || [];

  const { hasPermission } = useCapabilities();
  const canAdminPublishJob = useCanAdminPublishJob();
  const { searchValue, debouncedSearch, setSearchValue, clearSearch } = useSearchWithParams({
    paramName: "searchTerm"
  });
  const { control, reset: resetFilters } = useForm({
    defaultValues: {
      stage: [] as string[],
      dates: { startDate: null, endDate: null },
      sectionId: "",
      serviceLevel: [] as string[],
      status: [] as string[],
      employerId: ""
    }
  });
  const [variables, setVariables] = useState<JobsQueryVariables>({});
  const { paginationVariables, createHeaderPagination, createFooterPagination, resetPagination } = usePagination({
    defaultPageSize: 100
  });

  const canManageJobPosts = useHasCapabilityBool({
    domain: DOMAIN.JOBS,
    permission: PERMISSION.manageJobPosts
  });

  const [selectedStageValues, setSelectedStageValues] = useState<string[]>(
    (otherParams?.stage || defaultStages).map((stage: string) => stage)
  );
  const [selectedJobStatusValues, setSelectedJobStatusValues] = useState<string[]>(
    (otherParams?.status || defaultStatuses).map((status: string) => status)
  );
  const [selectedServiceLevelValues, setSelectedServiceLevelValues] = useState<string[]>(
    (otherParams?.serviceLevel || defaultServiceLevels).map((level: string) => level)
  );

  const [patchJobPost] = useMutation(UPDATE_JOB_POST_MUTATION);
  const [deleteJobPost] = useMutation(DELETE_JOB_POST_MUTATION);
  const [sendJobPostReviewRequestMutation, { loading: sendingReviewRequest }] =
    useMutation(SEND_REVIEW_REQUEST_MUTATION);

  const [sendReviewRequestOpen, setSendReviewRequestOpen] = useState(false);
  const [selectedJobForReview, setSelectedJobForReview] = useState<{
    id: string;
    notes: string;
  }>({
    id: "",
    notes: ""
  });

  const [showPublishDialog, setShowPublishDialog] = useState(false);
  const [isAdminPublish, setIsAdminPublish] = useState(false);
  const [showUnpublishDialog, setShowUnpublishDialog] = useState(false);
  const [showRevertToDraftDialog, setShowRevertToDraftDialog] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState<string>("");
  const [selectedJobInfo, setSelectedJobInfo] = useState({
    title: "",
    facility: ""
  });
  const [isPublishing, setIsPublishing] = useState(false);
  const [isUnpublishing, setIsUnpublishing] = useState(false);
  const [isRevertingToDraft, setIsRevertingToDraft] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showDownloadApplicantsDialog, setShowDownloadApplicantsDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showRenewJobPostDialog, setShowRenewJobPostDialog] = useState(false);
  const [isRenewing, setIsRenewing] = useState(false);
  const [isMobileSortOpen, setIsMobileSortOpen] = useState(false);

  // Simple loading states for job actions
  const [loadingJobIds, setLoadingJobIds] = useState<Set<string>>(new Set());

  // Use the same query as the applicants page so we can use cached data
  // Reviews are handled separately in the reviews tab, so we don't need the reviews query here
  const {
    data: applicationData,
    loading: isLoadingApplicants,
    error: applicantsError
  } = useQuery<{ jobPost: JobPost }>(JOB_POST_WITH_APPLICATIONS_QUERY, {
    variables: { jobPostId: selectedJobId },
    skip: !selectedJobId || !showDownloadApplicantsDialog,
    fetchPolicy: "cache-first"
  });

  const { data: selectedJobData } = useQuery<{ jobPost: JobPost }>(JOB_POST_DETAILS_QUERY, {
    variables: { jobPostId: selectedJobId },
    skip: !selectedJobId || !showRenewJobPostDialog,
    fetchPolicy: "cache-first"
  });

  const [showConvertModal, setShowConvertModal] = useState(false);
  const [convertedJobData, setConvertedJobData] = useState<any>(null);

  const { Menu, setMenu } = useTableRowMenu();
  const navigate = useNavigate();
  const { hasJobData } = useConvertedJobData();

  const compileBaseJobPostPath = useCallback(() => {
    const currentPath = location.pathname;
    let basePath = "";

    // Core paths should operate on themselves for routing.
    if (currentPath.includes("/jobs/") || currentPath.includes("/facilities/") || currentPath.includes("/sections/")) {
      basePath = currentPath.split("/").slice(0, -1).join("/");
    } else {
      // Send non-traditional routes to generic "jobs" path.
      basePath = "/jobs";
    }
    return `${basePath}/job-post`;
  }, [location]);

  // Check if there's convertedJobData in localStorage and navigate to create job page if there is
  useEffect(() => {
    if (hasJobData) {
      const basePath = compileBaseJobPostPath();
      const createPath = `${basePath}/create`;
      navigate(createPath as Path);
    }
  }, [hasJobData, navigate, location, compileBaseJobPostPath]);

  const { uploadFile } = useAgentFileUpload({
    agentName: "job-upload-extractor",
    content: "Process this job posting file",
    onSuccess: (result) => {
      if (!conversionCancelledRef.current) {
        localStorage.setItem("convertedJobData", JSON.stringify(result));
        setConvertedJobData(result);
        showSnackbar("File uploaded successfully. Swing Away! 🏌️");
      }
    },
    onError: (error) => {
      showSnackbar(error.message);
    }
  });

  useEffect(() => {
    const paramSectionId = defaultValues?.sectionId || params.sectionId;
    const defaultState = {
      ...(paramSectionId ? { sectionId: paramSectionId } : {}),
      ...(defaultValues?.facilityId ? { facilityId: defaultValues?.facilityId } : {}),
      ...(!otherParams?.stage ? { stage: defaultValues?.stage || defaultStages } : {}),
      ...(!otherParams?.status ? { status: defaultStatuses } : {}),
      ...(!otherParams?.serviceLevel ? { serviceLevel: defaultServiceLevels } : {}),
      ...(defaultValues?.ownerId ? { ownerId: defaultValues.ownerId } : {}),
      ...(defaultValues?.employerId ? { employerId: defaultValues.employerId } : {}),
      ...otherParams,
      ...(dateRange ? dateRange : {})
    };
    setVariables({ ...defaultState });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValues]);

  useEffect(() => {
    setVariables((prev) => ({
      ...prev,
      searchTerm: debouncedSearch || undefined
    }));

    if (debouncedSearch) {
      resetPagination();
    }
  }, [debouncedSearch, resetPagination]);

  const isStageDefault = (stages?: string[]) => {
    if (!stages) return true;
    if (stages.length !== defaultStages.length) return false;
    return stages.every((stage) => defaultStages.includes(stage as PostStage));
  };

  const isDefaultLowerDateRange = (lowerDateRange?: DateType) => {
    if (lowerDateRange !== defaultValues?.lowerDateRange) return false;
    return true;
  };

  const isDefaultUpperDateRange = (upperDateRange?: DateType) => {
    if (upperDateRange !== defaultValues?.upperDateRange) return false;
    return true;
  };

  const isDefaultStatus = (statuses: string[]) => {
    if (statuses.length !== defaultStatuses.length) return false;
    return statuses.every((status) => defaultStatuses.includes(status));
  };

  // Simple helper functions for loading states
  const setJobLoading = (jobId: string, loading: boolean) => {
    setLoadingJobIds((prev) => {
      const newSet = new Set(prev);
      if (loading) {
        newSet.add(jobId);
      } else {
        newSet.delete(jobId);
      }
      return newSet;
    });
  };

  const [orderBy, setOrderBy] = useState<ColumnSort[]>(defaultSort || []);

  const getQueryVariables = (): JobsQueryVariables => {
    // section may exist in variables from URL params but isn't a valid query variable, so we need strip it out
    const { section, ...queryVars } = variables as JobsQueryVariables & {
      section?: string;
    };
    return queryVars;
  };

  const getOrderByVariables = (): JobsQueryOrderByVariables => {
    const { id, desc } = orderBy[0] || {};
    return id
      ? {
          orderBy: {
            field: snakeCase(id).toUpperCase(),
            direction: desc ? "DESC" : "ASC"
          }
        }
      : {};
  };

  const { data, loading, refetch } = useQuery(JOBS_SEARCH_QUERY, {
    variables: {
      ...paginationVariables,
      input: getQueryVariables(),
      ...getOrderByVariables()
    },
    skip: Object.keys(variables).length === 0
  });

  // Cleanup loading states when component unmounts
  useEffect(() => {
    return () => {
      setLoadingJobIds(new Set());
    };
  }, []);

  const mapJobs = (data: any[]): JobPostRow[] =>
    (data || []).map(
      ({
        name,
        facility,
        section,
        serviceLevel,
        owner,
        employer,
        pointOfContact,
        createdAt,
        goLiveTime,
        jobPostPublishTime,
        submissionDeadline,
        numberOfApplicants,
        pgaOrgUrl,
        externalJobUrl,
        stage,
        status,
        reviewRequestSent,
        applyVia,
        compensation,
        ...jobPost
      }) => {
        return {
          ...jobPost,
          title: name,
          facility: facility?.name,
          section: section?.name,
          serviceLevel: serviceLevel as ServiceLevel,
          totalCompensation: compensation?.totalAnticipated || null,
          employer: employer?.name || pointOfContact?.name,
          owner: owner?.name,
          createdAt,
          goLiveTime: jobPostPublishTime || goLiveTime,
          submissionDeadline,
          applicantCount:
            applyVia === "Facility Website" &&
            serviceLevel !== ServiceLevel.EXECUTIVE &&
            serviceLevel !== ServiceLevel.ADVANCED
              ? "External"
              : numberOfApplicants,
          applicationUrl: externalJobUrl || pgaOrgUrl,
          stage: stage as PostStage,
          status,
          reviewRequestSent
        };
      }
    );

  const jobPostData: JobPostRow[] = mapJobs(data?.jobPosts?.nodes || []);

  const { postingStage, jobStatus, serviceLevel, postedDateRange, section } = filters;
  const {
    title,
    facility,
    section: sectionHeader,
    serviceLevel: serviceLevelHeader,
    totalCompensation: totalCompensationHeader,
    employer,
    owner,
    postedOn,
    submissionDeadline,
    applicantCount,
    postingStage: postingStageHeader,
    jobStatus: jobStatusHeader
  } = columnHeaders;

  function onClickDuplicateJob(row: any) {
    const basePath = compileBaseJobPostPath();
    const createPath = `${basePath}/create?duplicate=${row.original.id}`;

    navigate(createPath as Path);
    window.scrollTo(0, 0);
  }

  function onClickViewPublishedPage(row: any) {
    window.open(row.original.applicationUrl, "_blank", "noopener,noreferrer");
  }

  const handleCreateJobClick = () => {
    const basePath = compileBaseJobPostPath();
    const createPath = `${basePath}/create`;

    navigate(createPath as Path);
    window.scrollTo(0, 0);
  };

  const createJobButton = () => (
    <Button icon="add" onClick={handleCreateJobClick} testId="create-job-button">
      Create Job
    </Button>
  );

  const UploadJobButton = () => (
    <Button
      variant="outlined"
      icon={<PGAAssistantIcon size={24} fill={sysColors["sys-brand-primary"]} />}
      onClick={() => {
        // Force any lingering focus-trap or modal elements to be cleaned up
        document.querySelectorAll(".focus-trap-wrapper").forEach((el) => el.remove());
        document.querySelectorAll('[aria-hidden="true"]').forEach((el) => {
          if (el.hasAttribute("data-modal-backdrop")) {
            el.remove();
          }
        });
        setConvertedJobData(null);
        conversionCancelledRef.current = false;

        // Set timeout to ensure DOM cleanup happens before opening new modal
        setTimeout(() => {
          setShowConvertModal(true);
        }, 10);
      }}
      testId="upload-job-button"
    >
      Upload Job
    </Button>
  );

  let buttonComps: (() => JSX.Element)[] = [];
  if (hasPermission(PERMISSION.manageJobPosts)) {
    buttonComps.push(UploadJobButton);
    buttonComps.push(createJobButton);
  }

  const handleSort = (updaterOrValue: any) => {
    const sortState = typeof updaterOrValue === "function" ? updaterOrValue([]) : updaterOrValue;
    const { id, desc } = sortState[0] || {};
    setOrderBy([{ id, desc }]);
  };

  const { facilityId, sectionId, ...selectedFilters } = variables;
  const isDefaultServiceLevel = (levels: string[]) => {
    if (levels.length !== defaultServiceLevels.length) return false;
    return levels.every((l) => defaultServiceLevels.includes(l));
  };
  const activeFilters = {
    stage: !isStageDefault((selectedFilters as JobsQueryVariables).stage),
    dates:
      !isDefaultLowerDateRange((selectedFilters as JobsQueryVariables).lowerDateRange) &&
      !isDefaultUpperDateRange((selectedFilters as JobsQueryVariables).upperDateRange),
    serviceLevel: !isDefaultServiceLevel(selectedServiceLevelValues),
    status: !isDefaultStatus(selectedJobStatusValues),
    sectionId: !!variables.sectionId
  };

  const removeFilter = (obj: any, keyToRemove: any) => {
    const { [keyToRemove]: _, ...rest } = obj;
    return Object.assign({}, rest);
  };

  function navigateToJobPost(jobPostId: string) {
    const basePath = compileBaseJobPostPath();
    const createPath = `${basePath}/${jobPostId}/details`;

    window.location.href = createPath;
  }

  const clearAllFilters = () => {
    deleteSearchParams(["dateRange", "stage[]", "status[]", "serviceLevel[]", "searchTerm", "sectionId", "section"]);

    setSelectedStageValues(defaultStages.map((stage) => stage));
    setSelectedJobStatusValues(defaultStatuses.map((status) => status));
    setSelectedServiceLevelValues(defaultServiceLevels.map((level) => level));

    clearSearch();
    resetFilters({ dates: { startDate: null, endDate: null } as any });

    const paramSectionId = defaultValues?.sectionId || params.sectionId;
    const defaultState = {
      ...(paramSectionId ? { sectionId: paramSectionId } : {}),
      ...(defaultValues?.facilityId ? { facilityId: defaultValues?.facilityId } : {}),
      ...(!otherParams?.stage ? { stage: defaultStages } : {}),
      ...(!otherParams?.status ? { status: defaultStatuses } : {}),
      ...(!otherParams?.serviceLevel ? { serviceLevel: defaultServiceLevels } : {}),
      ...(defaultValues?.ownerId ? { ownerId: defaultValues.ownerId } : {}),
      ...(defaultValues?.employerId ? { employerId: defaultValues.employerId } : {}),
      ...(dateRange ? dateRange : {})
    };

    setVariables({ ...defaultState });
    resetPagination();
  };

  async function handlePublishJob(jobId: string) {
    const config = JOB_ACTION_CONFIGS.publish;
    try {
      setIsPublishing(true);
      setJobLoading(jobId, true);

      const response = await patchJobPost({
        variables: {
          input: {
            id: jobId,
            stage: config.stage,
            ...(isAdminPublish ? { termOfServiceAgreement: true } : {})
          }
        }
      });

      if (response.data.patchJobPost.success) {
        showSnackbar(config.successMessage);
        refetch();
      } else {
        showSnackbar(response.data.patchJobPost.message || `Error ${config.errorAction}`);
      }
    } catch (error: any) {
      showSnackbar(error.message || `Error ${config.errorAction}`);
    } finally {
      setIsPublishing(false);
      setJobLoading(jobId, false);
      setShowPublishDialog(false);
      setSelectedJobId("");
      setIsAdminPublish(false);
    }
  }

  async function handleUnpublishJob(jobId: string) {
    const config = JOB_ACTION_CONFIGS.unpublish;
    try {
      setIsUnpublishing(true);
      const response = await patchJobPost({
        variables: {
          input: {
            id: jobId,
            stage: config.stage
          }
        }
      });

      if (response.data.patchJobPost.success) {
        showSnackbar(config.successMessage);
        refetch();
      } else {
        showSnackbar(response.data.patchJobPost.message || `Error ${config.errorAction}`);
      }
    } catch (error: any) {
      showSnackbar(error.message || `Error ${config.errorAction}`);
    } finally {
      setIsUnpublishing(false);
      setShowUnpublishDialog(false);
      setSelectedJobId("");
    }
  }

  async function handleRevertToDraft(jobId: string) {
    const config = JOB_ACTION_CONFIGS.revertToDraft;
    try {
      setIsRevertingToDraft(true);
      setJobLoading(jobId, true);

      const response = await patchJobPost({
        variables: {
          input: {
            id: jobId,
            stage: config.stage
          }
        }
      });

      if (response.data.patchJobPost.success) {
        showSnackbar(config.successMessage);
        refetch();
      } else {
        showSnackbar(response.data.patchJobPost.message || `Error ${config.errorAction}`);
      }
    } catch (error: any) {
      showSnackbar(error.message || `Error ${config.errorAction}`);
    } finally {
      setIsRevertingToDraft(false);
      setJobLoading(jobId, false);
      setShowRevertToDraftDialog(false);
      setSelectedJobId("");
    }
  }

  async function handleSendForReview(jobId: string, notes: string) {
    try {
      const response = await sendJobPostReviewRequestMutation({
        variables: {
          input: {
            id: jobId,
            notes
          }
        }
      });

      if (response.data?.sendJobPostReviewRequest?.success) {
        showSnackbar("Review request has been sent");
        setSendReviewRequestOpen(false);
        setSelectedJobForReview({ id: "", notes: "" });
        refetch();
      } else {
        showSnackbar(
          response.data?.sendJobPostReviewRequest?.message ||
            "There was a problem sending this request for review. Please try again later."
        );
      }
    } catch (error: any) {
      showSnackbar("There was a problem sending this request for review. Please try again later.");
    }
  }

  async function handleDeleteJob(jobId: string) {
    setIsDeleting(true);
    setJobLoading(jobId, true);
    try {
      const response = await deleteJobPost({
        variables: {
          jobPostId: jobId
        }
      });

      if (response.data.deleteJobPost.success) {
        showSnackbar("Job deleted successfully");
        refetch();
      } else {
        showSnackbar(response.data.deleteJobPost.message || "Error deleting job");
      }
    } catch (error: any) {
      showSnackbar("There was a problem deleting this job. Please try again later.");
    } finally {
      setIsDeleting(false);
      setJobLoading(jobId, false);
      setShowDeleteDialog(false);
      setSelectedJobId("");
    }
  }

  async function handleRenewJobPost(updatedDeadline: string) {
    setIsRenewing(true);
    setJobLoading(selectedJobId, true);
    try {
      // In full-cutover mode, explicitly set stage, status, deadline, and publish time
      const { data: renewData } = await patchJobPost({
        variables: {
          input: {
            id: selectedJobId,
            stage: PostStage.ACTIVE,
            status: HiringStatus.IN_PROGRESS,
            submissionDeadline: updatedDeadline,
            jobPostPublishTime: new Date().toISOString()
          }
        }
      });

      if (renewData?.patchJobPost?.success) {
        showSnackbar("Job post has been renewed successfully");
        refetch();
        setShowRenewJobPostDialog(false);
        setSelectedJobId("");
      } else {
        showSnackbar(
          renewData?.patchJobPost?.message || "There was a problem renewing this job post. Please try again later."
        );
      }
    } catch (error) {
      showSnackbar("There was a problem renewing this job post. Please try again later.");
    } finally {
      setIsRenewing(false);
      setJobLoading(selectedJobId, false);
    }
  }

  const canPublishJob = (stage: PostStage, serviceLevel: ServiceLevel) => {
    if (!canManageJobPosts) return false;
    const publishableStages = [
      PostStage.REMOVED,
      PostStage.DRAFT,
      PostStage.PENDING,
      PostStage.IN_REVIEW,
      PostStage.ASSIST
    ];
    const publishableServiceLevels = [ServiceLevel.ADVANCED, ServiceLevel.EXECUTIVE, ServiceLevel.SELF_SERVE];
    return publishableStages.includes(stage) && publishableServiceLevels.includes(serviceLevel);
  };

  const canUnpublishJob = (stage: PostStage) => {
    if (!canManageJobPosts) return false;
    return [PostStage.ACTIVE, PostStage.INACTIVE].includes(stage);
  };

  const canSendForReview = (stage: PostStage, serviceLevel: ServiceLevel, reviewRequestSent?: string | null) => {
    if (!canManageJobPosts) return false;
    const isDraft = stage === PostStage.DRAFT;
    const isAssisted = serviceLevel === ServiceLevel.ASSISTED;
    const isReview = stage === PostStage.IN_REVIEW;

    return isAssisted && (isReview || (isDraft && !!reviewRequestSent));
  };

  const onClickViewJobApplicants = (row: any) => {
    const basePath = compileBaseJobPostPath();
    const createPath = `${basePath}/${row?.id}/applicants`;

    window.location.href = createPath;
  };

  const activeFiltersCount = Object.values(activeFilters).filter(Boolean).length;

  return (
    <>
      <Menu />
      <ConvertDocumentToJobModal
        open={showConvertModal}
        onClose={() => {
          setShowConvertModal(false);
          setConvertedJobData(null);
          conversionCancelledRef.current = true;
        }}
        onUpload={uploadFile}
        convertedJobData={convertedJobData}
      />
      <Table
        data={jobPostData}
        loading={loading}
        getRowLink={(row) => {
          const basePath = compileBaseJobPostPath();
          const createPath = `${basePath}/${row.original.id}/details`;
          return { to: createPath as Path };
        }}
        onSortingChange={handleSort}
        sortingState={orderBy}
        serverSideSorting={true}
        renderDetails={() => <TableDetails heading={tableHeading} body={tableDescription} ButtonComps={buttonComps} />}
        renderControls={(_table, { onSortClick }) => (
          <TableControls
            pagination={createHeaderPagination(data?.jobPosts, {
              pageCount: jobPostData?.length,
              loading
            })}
            useExpandedFilters={true}
            onSortClick={onSortClick}
            expandedFiltersState={activeFilters}
            onClearAllFilters={clearAllFilters}
            activeFilters={activeFiltersCount}
          >
            <SearchInput
              placeholder="Search Jobs"
              query={searchValue}
              onClear={clearSearch}
              onChange={setSearchValue}
            />
            {postingStage && (
              <Controller
                name="stage"
                control={control}
                render={() => (
                  <MultiSelect
                    label="Posting Stage"
                    selectedValues={selectedStageValues}
                    options={[
                      { value: PostStage.DRAFT, label: "Draft" },
                      { value: PostStage.PENDING, label: "Pending" },
                      { value: PostStage.IN_REVIEW, label: "In Review" },
                      { value: PostStage.ACTIVE, label: "Active" },
                      { value: PostStage.ASSIST, label: "Assist" },
                      { value: PostStage.INACTIVE, label: "Inactive" },
                      { value: PostStage.REMOVED, label: "Removed" },
                      { value: PostStage.UNPOSTED, label: "Unposted" }
                    ]}
                    onSelect={(value: string, checked) => {
                      const stage = checked
                        ? [...selectedStageValues, value]
                        : selectedStageValues.filter((v) => v !== value);
                      const values = stage.length > 0 ? { ...variables, stage } : removeFilter(variables, "stage");
                      setVariables({ ...values });
                      setSelectedStageValues(stage);
                      addSearchParam("stage[]", stage.join(","));
                      resetPagination();
                    }}
                    onClear={() => {
                      const { stage, ...rest } = variables;
                      setVariables({
                        ...rest
                      });
                      setSelectedStageValues([]);
                      deleteSearchParams("stage[]");
                      resetPagination();
                    }}
                  />
                )}
              />
            )}
            {postedDateRange && (
              <div data-name="dates" data-filter-size="custom">
                <Controller
                  name="dates"
                  control={control}
                  render={({ field }) => (
                    <DateSelect
                      label="Posted Date Range"
                      placeholder="Show All"
                      useModal={true}
                      useDefaultShortcuts={true}
                      dateLooking="backward"
                      hideClearButton={true}
                      allowClear={false}
                      startFrom={
                        params.dateRange?.lowerDateRange ? new Date(params.dateRange.lowerDateRange) : undefined
                      }
                      value={
                        params.dateRange?.lowerDateRange && params.dateRange?.upperDateRange
                          ? {
                              startDate: params.dateRange.lowerDateRange.split("T")[0],
                              endDate: params.dateRange.upperDateRange.split("T")[0]
                            }
                          : field.value
                      }
                      onChange={(e) => {
                        field.onChange(e);
                        if (typeof e === "object" && e !== null) {
                          setVariables({
                            ...variables,
                            lowerDateRange: e.startDate ? e.startDate : null,
                            upperDateRange: e.endDate ? e.endDate : null
                          });
                          if (e.startDate && e.endDate) {
                            addSearchParam("dateRange", [e.startDate, e.endDate].join(","));
                            resetPagination();
                          }
                        }
                      }}
                    />
                  )}
                />
              </div>
            )}
            {section && (
              <Controller
                name="sectionId"
                control={control}
                render={() => (
                  <SectionAutocomplete
                    params={params}
                    addSearchParam={(key, value) => {
                      if (key === "sectionId") {
                        setVariables({ ...variables, sectionId: value });
                      }
                      addSearchParam(key, value);
                      resetPagination();
                    }}
                    deleteSearchParams={(e) => {
                      deleteSearchParams(e);
                    }}
                  />
                )}
              />
            )}
            {serviceLevel && (
              <Controller
                name="serviceLevel"
                control={control}
                render={() => (
                  <MultiSelect
                    label="Service Level"
                    selectedValues={selectedServiceLevelValues}
                    options={[
                      { value: "Self-Serve", label: "Self-Serve" },
                      { value: "Assisted", label: "Assisted" },
                      { value: "Advanced", label: "Advanced" },
                      { value: "Executive", label: "Executive" }
                    ]}
                    onSelect={(value: string, checked) => {
                      const serviceLevel = checked
                        ? [...selectedServiceLevelValues, value]
                        : selectedServiceLevelValues.filter((v) => v !== value);
                      const values =
                        serviceLevel.length > 0
                          ? { ...variables, serviceLevel }
                          : removeFilter(variables, "serviceLevel");
                      setVariables({ ...values });
                      setSelectedServiceLevelValues(serviceLevel);
                      addSearchParam("serviceLevel[]", serviceLevel.join(","));
                      resetPagination();
                    }}
                    onClear={() => {
                      const { serviceLevel, ...rest } = variables;
                      setVariables({
                        ...rest
                      });
                      setSelectedServiceLevelValues([]);
                      deleteSearchParams("serviceLevel[]");
                      resetPagination();
                    }}
                  />
                )}
              />
            )}
            {jobStatus && (
              <Controller
                name="status"
                control={control}
                render={() => (
                  <MultiSelect
                    label="Job Status"
                    selectedValues={selectedJobStatusValues}
                    options={[
                      { value: "Filled", label: "Filled" },
                      { value: "Incomplete", label: "Incomplete" },
                      { value: "In Progress", label: "In Progress" },
                      { value: "Expired", label: "Expired" }
                    ]}
                    onSelect={(value: string, checked) => {
                      const status = checked
                        ? [...selectedJobStatusValues, value]
                        : selectedJobStatusValues.filter((v) => v !== value);
                      const values = status.length > 0 ? { ...variables, status } : removeFilter(variables, "status");
                      setVariables({ ...values });
                      setSelectedJobStatusValues(status);
                      addSearchParam("status[]", status.join(","));
                      resetPagination();
                    }}
                    onClear={() => {
                      const { status, ...rest } = variables;
                      setVariables({
                        ...rest
                      });
                      setSelectedJobStatusValues([]);
                      deleteSearchParams("status[]");
                      resetPagination();
                    }}
                  />
                )}
              />
            )}
          </TableControls>
        )}
        columns={[
          ...(title
            ? [
                {
                  accessorKey: "title",
                  header: "Job Title",
                  enableSorting: true,
                  meta: {
                    className: getStickyColumnClasses(true)
                  }
                }
              ]
            : []),
          ...(facility
            ? [
                {
                  accessorKey: "facility",
                  header: "Facility",
                  enableSorting: true
                }
              ]
            : []),
          ...(sectionHeader
            ? [
                {
                  accessorKey: "section",
                  header: "Section",
                  enableSorting: true
                }
              ]
            : []),
          ...(serviceLevelHeader
            ? [
                {
                  accessorKey: "serviceLevel",
                  header: "Service Level",
                  enableSorting: true
                }
              ]
            : []),
          ...(totalCompensationHeader
            ? [
                {
                  accessorKey: "totalCompensation",
                  header: "Total Comp",
                  enableSorting: true,
                  cell: ({ row: { original } }: { row: { original: any } }) => {
                    return <div>{formatTotalCompensation(original.totalCompensation)}</div>;
                  }
                }
              ]
            : []),
          ...(employer
            ? [
                {
                  accessorKey: "employer",
                  header: "Employer",
                  enableSorting: true
                }
              ]
            : []),
          ...(owner ? [{ accessorKey: "owner", header: "Owner", enableSorting: true }] : []),
          ...(postedOn
            ? [
                {
                  accessorKey: "goLiveTime",
                  header: "Posted On",
                  enableSorting: true,
                  cell({ row: { original } }: { row: { original: any } }) {
                    return (
                      <div>
                        {original.goLiveTime ? toDateFormat(original.goLiveTime, { tz: "utc" }) : "Not published"}
                      </div>
                    );
                  }
                }
              ]
            : []),
          ...(submissionDeadline
            ? [
                {
                  accessorKey: "submissionDeadline",
                  header: "Deadline",
                  enableSorting: true,
                  cell({ row: { original } }: { row: { original: any } }) {
                    return (
                      <div>
                        {toDateFormat(original.submissionDeadline, {
                          tz: "America/New_York"
                        })}
                      </div>
                    );
                  }
                }
              ]
            : []),
          ...(applicantCount && canManageJobPosts
            ? [
                {
                  accessorKey: "applicantCount",
                  header: "Applicants",
                  enableSorting: true,
                  sortingFn: () => 0,
                  cell: ({ row: { original } }: { row: { original: any } }) => {
                    return (
                      <div
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          onClickViewJobApplicants(original);
                        }}
                      >
                        {original.applyVia === "Facility Website" &&
                        original.serviceLevel !== ServiceLevel.EXECUTIVE &&
                        original.serviceLevel !== ServiceLevel.ADVANCED
                          ? "External"
                          : original.applicantCount}
                      </div>
                    );
                  }
                }
              ]
            : []),
          ...(postingStageHeader
            ? [
                {
                  header: "Posting Stage",
                  accessorKey: "stage",
                  enableSorting: true,
                  cell({ row: { original } }: { row: { original: any } }) {
                    const isLoading = loadingJobIds.has(original.id);
                    return (
                      <div className="flex items-center gap-2">
                        {renderPostStage(original.stage, original.reviewRequestSent)}
                        {isLoading && (
                          <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin"></div>
                        )}
                      </div>
                    );
                  }
                }
              ]
            : []),
          ...(jobStatusHeader
            ? [
                {
                  header: "Job Status",
                  accessorKey: "status",
                  enableSorting: true,
                  cell: ({ row: { original } }: { row: { original: any } }) => {
                    return <div>{original.status}</div>;
                  }
                }
              ]
            : []),
          buildActionColumn<JobPostRow>({
            actionId,
            setMenu,
            getRowId: (row) => row.id,
            getActions: (row) => {
              const stage = row.stage;
              const sl = row.serviceLevel;
              const isFilledOrIncomplete = row.status === "Filled" || row.status === "Incomplete";

              const showSendForReview = canSendForReview(stage, sl, row.reviewRequestSent);
              const showPublish = canPublishJob(stage, sl);
              const showAdminPublish = stage === PostStage.IN_REVIEW && canAdminPublishJob;
              const showUnpublish =
                (stage === PostStage.ACTIVE || stage === PostStage.INACTIVE) && canUnpublishJob(stage);
              const showRevertToDraft =
                (stage === PostStage.PENDING || stage === PostStage.ASSIST) &&
                canManageJobPosts &&
                canRevertToDraft(row.stage, row.status, row.serviceLevel);
              const showRenew = (stage === PostStage.ACTIVE || stage === PostStage.INACTIVE) && !isFilledOrIncomplete;
              const isDraftLike = stage === PostStage.DRAFT || stage === PostStage.REMOVED;
              const showManage = !isDraftLike;
              const showViewDetails = isDraftLike;
              const showPublishedPage = !!row.applicationUrl && !isDraftLike;
              const showDownloadApplicants = canManageJobPosts && (row.applicantCount as number) > 0;

              const actions: Action[] = [];

              if (showSendForReview) {
                actions.push({
                  text: "Send for Review",
                  section: "Manage",
                  onClick: () => {
                    setSelectedJobForReview({ id: row.id, notes: "" });
                    setSendReviewRequestOpen(true);
                  }
                });
              }
              if (showPublish) {
                actions.push({
                  text: "Publish Job",
                  section: "Manage",
                  onClick: () => {
                    setSelectedJobId(row.id);
                    setIsAdminPublish(false);
                    setShowPublishDialog(true);
                  }
                });
              }
              if (showAdminPublish) {
                actions.push({
                  text: "Admin Publish Job",
                  section: "Manage",
                  onClick: () => {
                    setSelectedJobId(row.id);
                    setIsAdminPublish(true);
                    setShowPublishDialog(true);
                  }
                });
              }
              if (showUnpublish) {
                actions.push({
                  text: "Unpublish Job",
                  section: "Manage",
                  onClick: () => {
                    setSelectedJobId(row.id);
                    setShowUnpublishDialog(true);
                  }
                });
              }
              if (showRevertToDraft) {
                actions.push({
                  text: "Revert to Draft",
                  section: "Manage",
                  onClick: () => {
                    setSelectedJobId(row.id);
                    setShowRevertToDraftDialog(true);
                  }
                });
              }
              if (showRenew) {
                actions.push({
                  text: "Renew Job Post",
                  section: "Manage",
                  onClick: () => {
                    setSelectedJobId(row.id);
                    setShowRenewJobPostDialog(true);
                  }
                });
              }
              if (showManage) {
                actions.push({
                  text: "Manage Job",
                  section: "Manage",
                  onClick: () => navigateToJobPost(row.id)
                });
              }
              if (showViewDetails) {
                actions.push({
                  text: "View Job Details",
                  section: "Manage",
                  onClick: () => navigateToJobPost(row.id)
                });
              }

              actions.push({
                text: "Duplicate Job",
                section: "Actions",
                onClick: () => onClickDuplicateJob({ original: row })
              });
              if (showDownloadApplicants) {
                actions.push({
                  text: "Applicants",
                  iconName: "download",
                  section: "Actions",
                  onClick: () => {
                    setSelectedJobId(row.id);
                    setSelectedJobInfo({ title: row.title, facility: row.facility });
                    setShowDownloadApplicantsDialog(true);
                  }
                });
              }

              if (showPublishedPage) {
                actions.push({
                  text: "Published Page",
                  iconName: "open_in_new",
                  section: "Sharing",
                  onClick: () => onClickViewPublishedPage({ original: row })
                });
              }

              if (canManageJobPosts) {
                actions.push({
                  text: "Delete Job",
                  iconName: "delete",
                  destructive: true,
                  onClick: () => {
                    setSelectedJobId(row.id);
                    setShowDeleteDialog(true);
                  }
                });
              }

              return actions;
            }
          })
        ]}
        renderFooter={() => (
          <TableFooter
            pagination={createFooterPagination(data?.jobPosts, {
              pageCount: jobPostData?.length,
              loading
            })}
          />
        )}
        hasActiveFilters={!!debouncedSearch || activeFiltersCount > 0}
        renderFilteredEmptyState={() => (
          <EmptyState title="No Results Found" caption="Try a different search or filter" iconName="business_center" />
        )}
        renderNoDataEmptyState={() => (
          <EmptyState title="No Data Found" caption="No job post records exist" iconName="business_center" />
        )}
      />
      <SendJobPostReviewModal
        open={sendReviewRequestOpen}
        setOpen={setSendReviewRequestOpen}
        sendJobPostReviewRequest={() => handleSendForReview(selectedJobForReview.id, selectedJobForReview.notes)}
        jobPostReviewRequestVariables={selectedJobForReview}
        setJobPostReviewRequestVariables={setSelectedJobForReview}
        disabled={sendingReviewRequest}
      />

      <QuickActionDialog
        open={showDeleteDialog}
        headline="Delete Job"
        message="Are you sure you want to delete this job? This cannot be undone."
        actionButtonText="Delete"
        onAction={() => handleDeleteJob(selectedJobId)}
        isLoading={isDeleting}
        actionButtonTheme="error"
        actionButtonIcon="delete"
        actionButtonTestId="delete-button"
        onClose={() => {
          setShowDeleteDialog(false);
          setSelectedJobId("");
        }}
      />

      <QuickActionDialog
        open={showPublishDialog}
        onClose={() => {
          setShowPublishDialog(false);
          setSelectedJobId("");
          setIsAdminPublish(false);
        }}
        headline={JOB_ACTION_CONFIGS.publish.headline}
        message={JOB_ACTION_CONFIGS.publish.modalMessage}
        actionButtonText={JOB_ACTION_CONFIGS.publish.buttonText}
        onAction={() => handlePublishJob(selectedJobId)}
        isLoading={isPublishing}
        actionButtonTestId="publish-button"
      />
      <QuickActionDialog
        open={showUnpublishDialog}
        onClose={() => {
          setShowUnpublishDialog(false);
          setSelectedJobId("");
        }}
        headline={JOB_ACTION_CONFIGS.unpublish.headline}
        message={JOB_ACTION_CONFIGS.unpublish.modalMessage}
        actionButtonText={JOB_ACTION_CONFIGS.unpublish.buttonText}
        onAction={() => handleUnpublishJob(selectedJobId)}
        isLoading={isUnpublishing}
        actionButtonTestId="unpublish-button"
      />
      <QuickActionDialog
        open={showRevertToDraftDialog}
        onClose={() => {
          setShowRevertToDraftDialog(false);
          setSelectedJobId("");
        }}
        headline={JOB_ACTION_CONFIGS.revertToDraft.headline}
        message={JOB_ACTION_CONFIGS.revertToDraft.modalMessage}
        actionButtonText={JOB_ACTION_CONFIGS.revertToDraft.buttonText}
        onAction={() => handleRevertToDraft(selectedJobId)}
        isLoading={isRevertingToDraft}
        actionButtonTestId="revert-to-draft-button"
      />

      <DownloadCSVDialog
        open={showDownloadApplicantsDialog}
        headline="Download Applicants"
        message="When your download is ready, click the button below to download your file. This may take a few moments."
        isLoading={isLoadingApplicants}
        error={applicantsError}
        data={applicationData?.jobPost?.applications || []}
        columnHeaders={CSVHeaders as string[]}
        fileTitle={`${selectedJobInfo?.title?.trim().replace(/\s+/g, "-") || "Job-Position"}-${
          selectedJobInfo?.facility?.trim().replace(/\s+/g, "-") || "Unknown-Facility"
        }`}
        generateColumnData={generateColumnData}
        actionButtonTestId="download-applicants-button"
        onClose={() => {
          setShowDownloadApplicantsDialog(false);
          setSelectedJobId("");
          setSelectedJobInfo({ title: "", facility: "" });
        }}
      />

      <RenewJobPostModal
        open={showRenewJobPostDialog}
        setOpen={setShowRenewJobPostDialog}
        handleRenew={handleRenewJobPost}
        disabled={isRenewing}
        currentSubmissionDeadline={selectedJobData?.jobPost?.submissionDeadline as string}
      />

      <MobileSortDrawer
        isOpen={isMobileSortOpen}
        onClose={() => setIsMobileSortOpen(false)}
        onSortChange={setOrderBy}
        currentSort={orderBy}
      />
    </>
  );
}
